import { apiClient } from '@app/lib'
import { useSugarContext } from '@app/providers'
import { type SugarProviderPhoto } from '@app/types'
import { useMutation, useQueryClient } from '@tanstack/react-query'

type UploadEnvelope =
  | { success: true; messages?: unknown; data: SugarProviderPhoto }
  | { success: false; messages?: { message?: string }[]; data: null }

export const useUploadProviderPhoto = () => {
  const queryClient = useQueryClient()
  const { siteId } = useSugarContext()

  const { isPending, mutateAsync } = useMutation({
    mutationFn: async ({ file }: { file: File }) => {
      if (!siteId) throw new Error('No site selected.')

      const formData = new FormData()
      formData.append('siteId', siteId)
      // Attestation gate lives in the UI; the server validates the field is
      // present and matches /^[tT][rR][uU][eE]$/.
      formData.append('attestation', 'true')
      formData.append('file', file, file.name)

      const { data } = await apiClient.post<UploadEnvelope>(
        '/api/provider-profile/photos',
        formData
      )

      if (!data.success) {
        throw new Error(data.messages?.[0]?.message ?? 'Upload failed.')
      }

      return data.data
    },
    onSuccess: () =>
      queryClient.invalidateQueries({ queryKey: ['provider-photos', { siteId }] })
  })

  return {
    isUploadingPhoto: isPending,
    uploadPhoto: mutateAsync
  }
}
